import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Activity, Award, Flame, BarChart3,
  ChevronDown, ChevronUp, Filter, RefreshCw
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import {
  getAllUsers,
  getAllQuizResults,
  getActivityLog,
  getAllStreaks,
  getUserStats
} from '../lib/supabase';
import { supabase } from '../config/supabase';
import { AVAILABLE_COURSES, getCourseList } from '../config/courses';
import { User, QuizResult, ActivityLog, DailyStreak, Course } from '../types';

type TabType = 'users' | 'activity' | 'quizzes' | 'progress' | 'streaks';
type SortDir = 'asc' | 'desc';

export function AdminDashboard() {
  const { user, isLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('users');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [quizResults, setQuizResults] = useState<(QuizResult & { user_name?: string })[]>([]);
  const [activityLog, setActivityLog] = useState<ActivityLog[]>([]);
  const [streaks, setStreaks] = useState<(DailyStreak & { user_name?: string })[]>([]);
  const [stats, setStats] = useState({ totalUsers: 0, totalQuizzes: 0, avgScore: 0, activeToday: 0 });

  // Filters
  const [userFilter, setUserFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');

  // Sorting
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // Load data callback
  const loadData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const [usersData, quizzesData, activityData, streaksData, statsData] = await Promise.all([
        getAllUsers(),
        getAllQuizResults(),
        getActivityLog(100, userFilter || undefined, actionFilter || undefined),
        getAllStreaks(),
        getUserStats(),
      ]);

      setUsers(usersData);
      setQuizResults(quizzesData);
      setActivityLog(activityData);
      setStreaks(streaksData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [userFilter, actionFilter]);

  // Load data effect
  useEffect(() => {
    if (user?.role === 'admin') {
      loadData();
    }
  }, [user, loadData]);

  // Sorted users - must be before early returns
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = (a as any)[sortField] || '';
      const bVal = (b as any)[sortField] || '';
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [users, sortField, sortDir]);

  // Sorted quizzes
  const sortedQuizzes = useMemo(() => {
    return [...quizResults].sort((a, b) => {
      const aVal = (a as any)[sortField] || '';
      const bVal = (b as any)[sortField] || '';
      const cmp = aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [quizResults, sortField, sortDir]);

  // User quiz averages
  const userQuizStats = useMemo(() => {
    const statsMap: Record<string, { count: number; total: number; avg: number }> = {};
    quizResults.forEach(q => {
      if (!statsMap[q.user_id]) {
        statsMap[q.user_id] = { count: 0, total: 0, avg: 0 };
      }
      statsMap[q.user_id].count++;
      statsMap[q.user_id].total += q.percentage;
      statsMap[q.user_id].avg = Math.round(statsMap[q.user_id].total / statsMap[q.user_id].count);
    });
    return statsMap;
  }, [quizResults]);

  // User streaks
  const userStreakStats = useMemo(() => {
    const statsMap: Record<string, { current: number; total: number }> = {};
    const userDates: Record<string, string[]> = {};

    streaks.forEach(s => {
      if (!userDates[s.user_id]) {
        userDates[s.user_id] = [];
      }
      userDates[s.user_id].push(s.date);
    });

    Object.entries(userDates).forEach(([userId, dates]) => {
      const sortedDates = dates.sort().reverse();
      let currentStreak = 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      for (let i = 0; i < sortedDates.length; i++) {
        const streakDate = new Date(sortedDates[i]);
        streakDate.setHours(0, 0, 0, 0);
        const expectedDate = new Date(today);
        expectedDate.setDate(expectedDate.getDate() - i);

        if (streakDate.getTime() === expectedDate.getTime()) {
          currentStreak++;
        } else {
          break;
        }
      }

      statsMap[userId] = { current: currentStreak, total: dates.length };
    });

    return statsMap;
  }, [streaks]);

  // Unique actions for filter
  const uniqueActions = useMemo(() => {
    const actions = new Set(activityLog.map(a => a.action));
    return Array.from(actions);
  }, [activityLog]);

  // Check if user is admin - AFTER all hooks
  if (isLoading) {
    return (
      <div className="admin-loading">
        <div className="spinner" />
        <p>Lade...</p>
      </div>
    );
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  // Sort helper
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format relative time
  const formatRelative = (dateStr: string | undefined) => {
    if (!dateStr) return 'Nie';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Min.`;
    if (diffHours < 24) return `vor ${diffHours} Std.`;
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return formatDate(dateStr);
  };

  // Action labels
  const actionLabels: Record<string, string> = {
    login: '🔐 Login',
    quiz: '📝 Quiz',
    quiz_start: '📝 Quiz gestartet',
    quiz_complete: '✅ Quiz beendet',
    written_quiz: '✍️ Schriftlich',
    flashcards: '🃏 Lernkarten',
    flashcard_session: '🃏 Lernkarten',
    lesson: '🎧 Lektion',
    lesson_start: '🎧 Lektion gestartet',
    lesson_complete: '✅ Lektion beendet',
    mindmap_view: '🗺️ Mindmap angesehen',
    fill_in: '📝 Lückentext',
    fill_in_quiz: '📝 Lückentext',
    transform_quiz: '🔄 Satzumwandlung',
    vocab_match: '🎯 Vokabel-Match',
    vocab_quiz: '📝 Vokabel-Quiz',
    text_lesson: '📖 Lernstoff',
    math_exercise: '🧮 Rechenaufgaben',
  };

  // Course name lookup
  const courseNames: Record<string, string> = {
    'deutsch-schularbeit': '📝 Deutsch',
    'english-test3': '🇬🇧 English',
    'mathe-schularbeit': '📐 Mathe',
    'eu-geographie': '🇪🇺 EU Geo',
  };

  const getCourseLabel = (details: any): string => {
    try {
      const d = typeof details === 'string' ? JSON.parse(details) : details;
      return courseNames[d?.course_id] || d?.course_id || '—';
    } catch { return '—'; }
  };

  // Format activity details for display
  const formatDetails = (action: string, details: any): string => {
    try {
      const d = typeof details === 'string' ? JSON.parse(details) : details;
      if (!d || Object.keys(d).length === 0) return '-';

      if (action === 'quiz') {
        return `${d.score}/${d.total} (${d.percentage}%) - ${d.duration_minutes || '?'} Min.`;
      }
      if (action === 'written_quiz') {
        return `${d.questions_answered || '?'} Fragen - ${d.duration_minutes || '?'} Min.`;
      }
      if (action === 'flashcards') {
        return `${d.cards_reviewed || '?'} Karten (${d.cards_known || '?'} gewusst)`;
      }
      if (action === 'lesson') {
        return d.lesson_id || 'gehört';
      }
      // New activity types
      if (action === 'fill_in' || action === 'fill_in_quiz') {
        return `${d.score || '?'}/${d.total || '?'} (${d.percentage || '?'}%)`;
      }
      if (action === 'transform_quiz') {
        return `${d.score || '?'}/${d.total || '?'} (${d.percentage || '?'}%)`;
      }
      if (action === 'vocab_match') {
        const errors = (d.attempts || 0) - (d.pairs_matched || 0);
        return `${d.set_title || d.set_id || '?'} – ${d.pairs_matched || '?'} Paare, ${errors > 0 ? errors : 0} Fehlversuche`;
      }
      if (action === 'vocab_quiz') {
        return `${d.score || '?'}/${d.total || '?'} (${d.percentage || '?'}%)`;
      }
      if (action === 'text_lesson') {
        return d.lesson_title || d.lesson_id || 'gelesen';
      }
      if (action === 'song_listen') {
        return `${d.song_title || '?'} – ${d.listened_percentage || 0}% gehoert`;
      }
      return JSON.stringify(d);
    } catch {
      return String(details || '-');
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <Link to="/profile" className="back-link">
          <ArrowLeft size={18} />
          Zurück
        </Link>
        <h1>🛡️ Admin Dashboard</h1>
        <button
          className="refresh-btn"
          onClick={loadData}
          disabled={isRefreshing}
        >
          <RefreshCw size={18} className={isRefreshing ? 'spinning' : ''} />
        </button>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats">
        <div className="stat-card">
          <Users size={24} />
          <div className="stat-info">
            <span className="stat-value">{stats.totalUsers}</span>
            <span className="stat-label">User</span>
          </div>
        </div>
        <div className="stat-card">
          <Award size={24} />
          <div className="stat-info">
            <span className="stat-value">{stats.totalQuizzes}</span>
            <span className="stat-label">Quizze</span>
          </div>
        </div>
        <div className="stat-card">
          <Activity size={24} />
          <div className="stat-info">
            <span className="stat-value">{stats.avgScore}%</span>
            <span className="stat-label">Ø Score</span>
          </div>
        </div>
        <div className="stat-card">
          <Flame size={24} />
          <div className="stat-info">
            <span className="stat-value">{stats.activeToday}</span>
            <span className="stat-label">Heute aktiv</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          <Users size={16} /> User
        </button>
        <button
          className={`tab ${activeTab === 'activity' ? 'active' : ''}`}
          onClick={() => setActiveTab('activity')}
        >
          <Activity size={16} /> Aktivität
        </button>
        <button
          className={`tab ${activeTab === 'quizzes' ? 'active' : ''}`}
          onClick={() => setActiveTab('quizzes')}
        >
          <Award size={16} /> Quizze
        </button>
        <button
          className={`tab ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          <BarChart3 size={16} /> Fortschritt
        </button>
        <button
          className={`tab ${activeTab === 'streaks' ? 'active' : ''}`}
          onClick={() => setActiveTab('streaks')}
        >
          <Flame size={16} /> Streaks
        </button>
      </div>

      {/* Content */}
      <div className="admin-content">
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('name')}>
                    Name <SortIcon field="name" />
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email <SortIcon field="email" />
                  </th>
                  <th onClick={() => handleSort('role')}>
                    Rolle <SortIcon field="role" />
                  </th>
                  <th onClick={() => handleSort('created_at')}>
                    Registriert <SortIcon field="created_at" />
                  </th>
                  <th onClick={() => handleSort('last_active')}>
                    Letzte Aktivität <SortIcon field="last_active" />
                  </th>
                  <th>Quiz Ø</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map(u => (
                  <tr key={u.id}>
                    <td className="user-name">{u.name}</td>
                    <td className="user-email">{u.email}</td>
                    <td>
                      <span className={`role-badge role-${u.role}`}>
                        {u.role === 'admin' ? '🛡️' : u.role === 'parent' ? '👨‍👩‍👧' : '🎓'} {u.role}
                      </span>
                    </td>
                    <td>{formatDate(u.created_at)}</td>
                    <td>{formatRelative(u.last_active)}</td>
                    <td>
                      {userQuizStats[u.id] ? (
                        <span className="quiz-avg">
                          {userQuizStats[u.id].avg}%
                          <small>({userQuizStats[u.id].count})</small>
                        </span>
                      ) : '-'}
                    </td>
                    <td>
                      {userStreakStats[u.id] ? (
                        <span className="streak-badge">
                          🔥 {userStreakStats[u.id].current}
                        </span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <>
            <div className="admin-filters">
              <div className="filter-group">
                <Filter size={16} />
                <select
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                >
                  <option value="">Alle User</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <select
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                >
                  <option value="">Alle Aktionen</option>
                  {uniqueActions.map(a => (
                    <option key={a} value={a}>{actionLabels[a] || a}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <select
                  value={courseFilter}
                  onChange={(e) => setCourseFilter(e.target.value)}
                >
                  <option value="">Alle Kurse</option>
                  <option value="deutsch-schularbeit">📝 Deutsch</option>
                  <option value="english-test3">🇬🇧 English</option>
                  <option value="mathe-schularbeit">📐 Mathe</option>
                  <option value="eu-geographie">🇪🇺 EU Geo</option>
                </select>
              </div>
            </div>
            <div className="admin-table-wrapper">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Zeitpunkt</th>
                    <th>User</th>
                    <th>Kurs</th>
                    <th>Aktion</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filtered = courseFilter
                      ? activityLog.filter(a => {
                          try {
                            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
                            return d?.course_id === courseFilter;
                          } catch { return false; }
                        })
                      : activityLog;
                    return filtered.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="no-data">
                          Keine Aktivitäten gefunden.
                        </td>
                      </tr>
                    ) : (
                      filtered.map(a => (
                        <tr key={a.id}>
                          <td>{formatDate(a.created_at)}</td>
                          <td className="user-name">{a.user_name || 'Unbekannt'}</td>
                          <td>
                            <span className="action-badge" style={{ fontSize: '10px' }}>
                              {getCourseLabel(a.details)}
                            </span>
                          </td>
                          <td>
                            <span className="action-badge">
                              {actionLabels[a.action] || a.action}
                            </span>
                          </td>
                          <td className="details-cell">
                            {formatDetails(a.action, a.details)}
                          </td>
                        </tr>
                      ))
                    );
                  })()}
                </tbody>
            </table>
            </div>
          </>
        )}

        {/* Quizzes Tab */}
        {activeTab === 'quizzes' && (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('created_at')}>
                    Datum <SortIcon field="created_at" />
                  </th>
                  <th>User</th>
                  <th onClick={() => handleSort('mode')}>
                    Modus <SortIcon field="mode" />
                  </th>
                  <th onClick={() => handleSort('score')}>
                    Score <SortIcon field="score" />
                  </th>
                  <th onClick={() => handleSort('percentage')}>
                    Prozent <SortIcon field="percentage" />
                  </th>
                  <th>Zeit</th>
                </tr>
              </thead>
              <tbody>
                {sortedQuizzes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="no-data">Keine Quiz-Ergebnisse vorhanden</td>
                  </tr>
                ) : (
                  sortedQuizzes.map(q => (
                    <tr key={q.id}>
                      <td>{formatDate(q.created_at)}</td>
                      <td className="user-name">{q.user_name || 'Unbekannt'}</td>
                      <td>
                        <span className={`mode-badge mode-${q.mode}`}>
                          {q.mode === 'test' ? '📋' : q.mode === 'random' ? '🎲' : '📝'} {q.mode}
                        </span>
                      </td>
                      <td>{q.score}/{q.total}</td>
                      <td>
                        <span className={`score-badge ${q.percentage >= 70 ? 'good' : q.percentage >= 50 ? 'ok' : 'bad'}`}>
                          {q.percentage}%
                        </span>
                      </td>
                      <td>{q.time_seconds ? `${Math.floor(q.time_seconds / 60)}:${String(q.time_seconds % 60).padStart(2, '0')}` : '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <AdminProgressTab users={users} activityLog={activityLog} formatRelative={formatRelative} />
        )}

        {/* Streaks Tab */}
        {activeTab === 'streaks' && (
          <div className="admin-table-wrapper">
            <h3 className="section-title">🔥 User Streaks</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Aktueller Streak</th>
                  <th>Gesamt Lerntage</th>
                  <th>Letzte Aktivität</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const streakData = userStreakStats[u.id] || { current: 0, total: 0 };
                  return (
                    <tr key={u.id}>
                      <td className="user-name">{u.name}</td>
                      <td>
                        <span className={`streak-value ${streakData.current >= 7 ? 'hot' : streakData.current >= 3 ? 'warm' : ''}`}>
                          🔥 {streakData.current} Tage
                        </span>
                      </td>
                      <td>{streakData.total} Tage</td>
                      <td>{formatRelative(u.last_active)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <h3 className="section-title">📅 Letzte Lernaktivitäten</h3>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>User</th>
                  <th>Aktivitäten</th>
                  <th>Lernzeit</th>
                </tr>
              </thead>
              <tbody>
                {streaks.slice(0, 20).map(s => (
                  <tr key={s.id}>
                    <td>{s.date}</td>
                    <td className="user-name">{s.user_name || 'Unbekannt'}</td>
                    <td>
                      {(s.activities || []).map((a, i) => (
                        <span key={i} className="activity-chip">{a}</span>
                      ))}
                    </td>
                    <td>{s.total_minutes} Min.</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin Progress Tab ─────────────────────────────────────────
// Shows per-user, per-course, per-topic progress with module tags
// Same visual style as CourseProgressCard on the homepage

type ModuleStatus = 'not_started' | 'in_progress' | 'completed';

interface TopicProgressAdmin {
  id: string;
  label: string;
  modules: { label: string; status: ModuleStatus; score: string }[];
  modulesCompleted: number;
  modulesTotal: number;
}

interface UserCourseProgress {
  userId: string;
  userName: string;
  courseId: string;
  topics: TopicProgressAdmin[];
  lastActivity: string | null;
}

function AdminProgressTab({ users, activityLog, formatRelative }: {
  users: User[];
  activityLog: ActivityLog[];
  formatRelative: (d: string | undefined) => string;
}) {
  const [progressData, setProgressData] = useState<Record<string, UserCourseProgress[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [expandedUsers, setExpandedUsers] = useState<Record<string, boolean>>({});
  const [selectedCourse, setSelectedCourse] = useState<string>('');

  const courseList = useMemo(() => getCourseList(true).filter(c => !c.hidden), []);

  useEffect(() => {
    loadAllProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAllProgress() {
    setIsLoading(true);
    try {
      // Load all courses
      const courses: Record<string, Course> = {};
      await Promise.all(
        AVAILABLE_COURSES.map(async (entry) => {
          try {
            courses[entry.id] = await entry.loader();
          } catch { /* skip failed loads */ }
        })
      );

      // Fetch all progress data in parallel (global, not per-user)
      const [allQuizRes, allFillInRes, allLessonRes, allFlashcardRes, allVocabMatchRes] = await Promise.all([
        supabase.from('quiz_results').select('user_id, course_id, percentage, wrong_questions, mode').order('created_at', { ascending: false }),
        supabase.from('activity_log').select('user_id, action, details, created_at').in('action', ['fill_in', 'vocab_match', 'math_exercise']).order('created_at', { ascending: false }),
        supabase.from('lesson_progress').select('user_id, course_id, lesson_id, completed'),
        supabase.from('flashcard_progress').select('user_id, course_id, flashcard_id, knew'),
        supabase.from('activity_log').select('user_id, details, created_at').eq('action', 'vocab_match').order('created_at', { ascending: false }),
      ]);

      // Find active users per course from activity log
      const userCourseMap: Record<string, Set<string>> = {};
      activityLog.forEach(a => {
        try {
          const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
          if (d?.course_id) {
            if (!userCourseMap[d.course_id]) userCourseMap[d.course_id] = new Set();
            userCourseMap[d.course_id].add(a.user_id);
          }
        } catch { /* skip */ }
      });

      // Also check quiz_results, flashcard_progress, lesson_progress for users
      allQuizRes.data?.forEach(q => {
        if (!userCourseMap[q.course_id]) userCourseMap[q.course_id] = new Set();
        userCourseMap[q.course_id].add(q.user_id);
      });
      allLessonRes.data?.forEach(l => {
        if (!userCourseMap[l.course_id]) userCourseMap[l.course_id] = new Set();
        userCourseMap[l.course_id].add(l.user_id);
      });
      allFlashcardRes.data?.forEach(f => {
        if (!userCourseMap[f.course_id]) userCourseMap[f.course_id] = new Set();
        userCourseMap[f.course_id].add(f.user_id);
      });

      // Build progress for each user × course
      const result: Record<string, UserCourseProgress[]> = {};

      for (const [courseId, course] of Object.entries(courses)) {
        if (!course.categories || course.categories.length === 0) continue;
        const activeUserIds = userCourseMap[courseId] ? Array.from(userCourseMap[courseId]) : [];
        if (activeUserIds.length === 0) continue;

        if (!result[courseId]) result[courseId] = [];

        for (const userId of activeUserIds) {
          const userName = users.find(u => u.id === userId)?.name || 'Unbekannt';

          // Filter data for this user + course
          const userQuiz = (allQuizRes.data || []).filter(q => q.user_id === userId && q.course_id === courseId && q.mode === 'quiz');
          const userFillIn = (allFillInRes.data || []).filter(a => {
            if (a.user_id !== userId || a.action !== 'fill_in') return false;
            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
            return d?.course_id === courseId;
          });
          const userLessons = (allLessonRes.data || []).filter(l => l.user_id === userId && l.course_id === courseId);
          const userFlashcards = (allFlashcardRes.data || []).filter(f => f.user_id === userId && f.course_id === courseId);
          const userVocabMatch = (allVocabMatchRes.data || []).filter(a => {
            if (a.user_id !== userId) return false;
            try {
              const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
              return d?.course_id === courseId;
            } catch { return false; }
          });
          const userMathEx = (allFillInRes.data || []).filter(a => {
            if (a.user_id !== userId || a.action !== 'math_exercise') return false;
            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
            return d?.course_id === courseId;
          });

          // Build category maps
          const questionsPerCat: Record<string, { quiz: number; fillIn: number }> = {};
          course.categories.forEach(cat => { questionsPerCat[cat.id] = { quiz: 0, fillIn: 0 }; });
          course.quizQuestions?.forEach(q => { if (questionsPerCat[q.category]) questionsPerCat[q.category].quiz++; });
          course.fillInQuestions?.forEach(q => { if (questionsPerCat[q.category]) questionsPerCat[q.category].fillIn++; });

          // Lessons per category
          const lessonsPerCat: Record<string, string[]> = {};
          course.textLessons?.forEach(l => {
            if (!lessonsPerCat[l.category]) lessonsPerCat[l.category] = [];
            lessonsPerCat[l.category].push(l.id);
          });
          const completedLessonIds = new Set(userLessons.filter(l => l.completed).map(l => l.lesson_id));

          // Flashcard category map
          const fcCategoryMap: Record<string, string> = {};
          course.flashcards?.forEach(f => { fcCategoryMap[f.id] = f.category; });
          const fcByCategory: Record<string, { known: number; weaknesses: number }> = {};
          userFlashcards.forEach(fp => {
            if (fp.flashcard_id.startsWith('vocab_')) return;
            const cat = fcCategoryMap[fp.flashcard_id];
            if (!cat) return;
            if (!fcByCategory[cat]) fcByCategory[cat] = { known: 0, weaknesses: 0 };
            if (fp.knew) fcByCategory[cat].known++;
            else fcByCategory[cat].weaknesses++;
          });

          // VocabMatch per category
          const setCategoryMap: Record<string, string> = {};
          course.vocabSets?.forEach(vs => { setCategoryMap[vs.id] = vs.category; });
          const vmDoneByCat: Record<string, boolean> = {};
          userVocabMatch.forEach(a => {
            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
            const cat = d?.set_id ? setCategoryMap[d.set_id] : undefined;
            if (cat) vmDoneByCat[cat] = true;
          });

          // FillIn best per category
          const fillInBestByCat: Record<string, number> = {};
          userFillIn.forEach(a => {
            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
            if (d?.category && d?.percentage !== undefined) {
              if (!fillInBestByCat[d.category] || d.percentage > fillInBestByCat[d.category]) {
                fillInBestByCat[d.category] = d.percentage;
              }
            }
          });

          // Quiz scores per category (from latest quiz wrong_questions)
          const categoryScores: Record<string, { correct: number; total: number }> = {};
          if (userQuiz.length > 0) {
            const latestQuiz = userQuiz[0];
            const wrongSet = new Set(latestQuiz.wrong_questions || []);
            course.quizQuestions?.forEach(q => {
              if (!categoryScores[q.category]) categoryScores[q.category] = { correct: 0, total: 0 };
              categoryScores[q.category].total++;
              if (!wrongSet.has(q.id)) categoryScores[q.category].correct++;
            });
          }

          // Math exercises per category
          const mathByCat: Record<string, boolean> = {};
          userMathEx.forEach(a => {
            const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
            if (d?.category) mathByCat[d.category] = true;
          });

          // Build topics
          const topics: TopicProgressAdmin[] = [];
          for (const cat of course.categories) {
            const qpc = questionsPerCat[cat.id] || { quiz: 0, fillIn: 0 };
            const catLessons = lessonsPerCat[cat.id] || [];
            const fcCount = course.flashcards?.filter(f => f.category === cat.id).length || 0;
            const vsCount = course.vocabSets?.filter(v => v.category === cat.id).length || 0;
            const meCount = course.mathExercises?.filter(m => m.category === cat.id).length || 0;

            // Skip empty topics
            if (qpc.quiz === 0 && qpc.fillIn === 0 && catLessons.length === 0 && fcCount === 0 && vsCount === 0 && meCount === 0) continue;

            const modules: { label: string; status: ModuleStatus; score: string }[] = [];
            let modulesCompleted = 0;
            let modulesTotal = 0;

            // Lesson
            if (catLessons.length > 0) {
              modulesTotal++;
              const allRead = catLessons.every(lid => completedLessonIds.has(lid));
              modules.push({ label: '📖 Lernstoff', status: allRead ? 'completed' : 'not_started', score: allRead ? '✓' : '—' });
              if (allRead) modulesCompleted++;
            }

            // FillIn — right after Lernstoff (linked exercise)
            if (qpc.fillIn > 0) {
              modulesTotal++;
              const best = fillInBestByCat[cat.id];
              let status: ModuleStatus = 'not_started';
              if (best !== undefined && best >= 90) { status = 'completed'; modulesCompleted++; }
              else if (best !== undefined) status = 'in_progress';
              modules.push({ label: '✏️ Lückentext', status, score: best !== undefined ? `${best}%` : '—' });
            }

            // Flashcards
            if (fcCount > 0) {
              modulesTotal++;
              const fc = fcByCategory[cat.id] || { known: 0, weaknesses: 0 };
              const practiced = fc.known + fc.weaknesses;
              let status: ModuleStatus = 'not_started';
              if (practiced > 0 && fc.weaknesses === 0) { status = 'completed'; modulesCompleted++; }
              else if (practiced > 0) status = 'in_progress';
              modules.push({ label: `🃏 ${fcCount} Karten`, status, score: practiced > 0 ? `${fc.known}/${fcCount}` : '—' });
            }

            // VocabMatch
            if (vsCount > 0) {
              modulesTotal++;
              const done = vmDoneByCat[cat.id] || false;
              modules.push({ label: '🔀 Matching', status: done ? 'completed' : 'not_started', score: done ? '✓' : '—' });
              if (done) modulesCompleted++;
            }

            // VocabQuiz (if enough flashcards)
            if (fcCount >= 4) {
              modulesTotal++;
              modules.push({ label: '🎯 Karten-Quiz', status: 'not_started', score: '—' });
            }

            // Quiz
            if (qpc.quiz > 0 && courseId !== 'deutsch-schularbeit') {
              modulesTotal++;
              const score = categoryScores[cat.id];
              let status: ModuleStatus = 'not_started';
              let pct = 0;
              if (score && score.total > 0) {
                pct = Math.round((score.correct / score.total) * 100);
                if (pct >= 90) { status = 'completed'; modulesCompleted++; }
                else status = 'in_progress';
              }
              modules.push({ label: '📝 Quiz', status, score: score ? `${pct}%` : '—' });
            }

            // Math exercises
            if (meCount > 0) {
              modulesTotal++;
              const done = mathByCat[cat.id] || false;
              modules.push({ label: `🧮 ${meCount} Aufgaben`, status: done ? 'completed' : 'not_started', score: done ? '✓' : '—' });
              if (done) modulesCompleted++;
            }

            topics.push({
              id: cat.id,
              label: cat.label,
              modules,
              modulesCompleted,
              modulesTotal: modulesTotal || 1,
            });
          }

          // Sort: incomplete first
          topics.sort((a, b) => {
            if (a.modulesCompleted < a.modulesTotal && b.modulesCompleted >= b.modulesTotal) return -1;
            if (a.modulesCompleted >= a.modulesTotal && b.modulesCompleted < b.modulesTotal) return 1;
            return 0;
          });

          // Last activity for this user+course
          const lastA = activityLog.find(a => {
            if (a.user_id !== userId) return false;
            try {
              const d = typeof a.details === 'string' ? JSON.parse(a.details) : a.details;
              return d?.course_id === courseId;
            } catch { return false; }
          });

          result[courseId].push({
            userId,
            userName,
            courseId,
            topics,
            lastActivity: lastA?.created_at || null,
          });
        }

        // Sort users by name
        result[courseId].sort((a, b) => a.userName.localeCompare(b.userName));
      }

      setProgressData(result);
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    setIsLoading(false);
  }

  const toggleUser = (key: string) => {
    setExpandedUsers(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getTopicIcon = (t: TopicProgressAdmin) => {
    if (t.modulesCompleted === 0) return '❌';
    if (t.modulesCompleted >= t.modulesTotal) return '✅';
    return '🔄';
  };

  if (isLoading) {
    return (
      <div className="loading" style={{ minHeight: '200px', padding: '40px', textAlign: 'center' }}>
        Lade Fortschrittsdaten...
      </div>
    );
  }

  const filteredCourses = selectedCourse
    ? courseList.filter(c => c.id === selectedCourse)
    : courseList;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
        <h3 className="section-title" style={{ margin: 0 }}>📊 Fortschritt nach Thema</h3>
        <select
          value={selectedCourse}
          onChange={(e) => setSelectedCourse(e.target.value)}
          style={{
            background: 'rgba(255,255,255,0.08)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '8px',
            padding: '6px 10px',
            fontSize: '13px',
          }}
        >
          <option value="">Alle Kurse</option>
          {courseList.map(c => (
            <option key={c.id} value={c.id}>{c.icon} {c.title}</option>
          ))}
        </select>
      </div>

      {filteredCourses.map(courseMeta => {
        const userProgressList = progressData[courseMeta.id] || [];
        if (userProgressList.length === 0) return null;

        return (
          <div key={courseMeta.id} style={{ marginBottom: '24px' }}>
            <h4 style={{ color: '#ffd54f', fontSize: '15px', marginBottom: '10px' }}>
              {courseMeta.icon} {courseMeta.title}
            </h4>

            {userProgressList.map(up => {
              const key = `${up.courseId}-${up.userId}`;
              const isExpanded = expandedUsers[key] !== false; // default expanded

              // Count total completed / total modules across all topics
              const totalCompleted = up.topics.reduce((s, t) => s + t.modulesCompleted, 0);
              const totalModules = up.topics.reduce((s, t) => s + t.modulesTotal, 0);
              const completionPct = totalModules > 0 ? Math.round((totalCompleted / totalModules) * 100) : 0;
              const completionColor = completionPct >= 90 ? '#4caf50' : completionPct >= 50 ? '#ff9800' : completionPct > 0 ? '#2196f3' : 'rgba(255,255,255,0.3)';

              return (
                <div key={key} style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  borderRadius: '12px',
                  marginBottom: '10px',
                  border: '1px solid rgba(255,255,255,0.08)',
                  overflow: 'hidden',
                }}>
                  {/* User header - clickable */}
                  <div
                    onClick={() => toggleUser(key)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 14px',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: '14px', fontWeight: 600, color: '#e8eaf6', flex: 1 }}>
                      🎓 {up.userName}
                    </span>
                    {/* Progress bar mini */}
                    <div style={{
                      width: '60px',
                      height: '6px',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${completionPct}%`,
                        height: '100%',
                        background: completionColor,
                        borderRadius: '3px',
                        transition: 'width 0.3s ease',
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', color: completionColor, minWidth: '35px', textAlign: 'right' }}>
                      {completionPct}%
                    </span>
                    <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>
                      {formatRelative(up.lastActivity || undefined)}
                    </span>
                    {isExpanded ? <ChevronUp size={16} style={{ color: 'rgba(255,255,255,0.3)' }} /> : <ChevronDown size={16} style={{ color: 'rgba(255,255,255,0.3)' }} />}
                  </div>

                  {/* Topic cards */}
                  {isExpanded && (
                    <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {up.topics.map(topic => (
                        <div
                          key={topic.id}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            background: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid rgba(255,255,255,0.06)',
                          }}
                        >
                          <span style={{ width: '18px', textAlign: 'center', flexShrink: 0, fontSize: '13px' }}>
                            {getTopicIcon(topic)}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: topic.modulesCompleted === 0 ? 'rgba(255,255,255,0.4)' : '#e8eaf6',
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {topic.label}
                            </div>
                            <div style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: '3px',
                              marginTop: '4px',
                            }}>
                              {topic.modules.map((mod, i) => {
                                const statusIcon = mod.status === 'completed' ? '✅' : mod.status === 'in_progress' ? '🔄' : '❌';
                                const statusColor = mod.status === 'completed' ? '#4caf50' : mod.status === 'in_progress' ? '#ff9800' : 'rgba(255,255,255,0.4)';
                                const statusBg = mod.status === 'completed' ? 'rgba(76, 175, 80, 0.15)' : mod.status === 'in_progress' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 255, 255, 0.04)';
                                const statusBorder = mod.status === 'completed' ? 'rgba(76, 175, 80, 0.3)' : mod.status === 'in_progress' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255,255,255,0.08)';
                                const scoreColor = mod.status === 'completed' ? '#4caf50' : mod.status === 'in_progress' ? '#ffd54f' : 'rgba(255,255,255,0.25)';
                                return (
                                  <span
                                    key={i}
                                    style={{
                                      fontSize: '10px',
                                      padding: '2px 5px 3px',
                                      borderRadius: '5px',
                                      background: statusBg,
                                      color: statusColor,
                                      border: `1px solid ${statusBorder}`,
                                      whiteSpace: 'nowrap',
                                      display: 'inline-flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      gap: '0px',
                                    }}
                                  >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <span style={{ fontSize: '8px' }}>{statusIcon}</span>
                                      {mod.label}
                                    </span>
                                    <span style={{ fontSize: '8px', color: scoreColor, lineHeight: 1 }}>
                                      {mod.score}
                                    </span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}

      {Object.keys(progressData).length === 0 && (
        <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', padding: '40px' }}>
          Keine Fortschrittsdaten vorhanden.
        </div>
      )}
    </div>
  );
}
